import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { 
  setupGlobalErrorHandlers, 
  setErrorContext, 
  addChatMessage, 
  clearChatContext,
  clearErrorContext,
  logError,
  logFrontendError,
  logProvisioningError,
  logDeploymentError,
  type ErrorContext,
  type ErrorSource,
  type ErrorSeverity,
} from '../lib/errorTracking';

interface ErrorTrackingContextValue {
  setContext: (context: Partial<ErrorContext>) => void;
  addChatMessage: (role: 'user' | 'assistant', content: string) => void;
  clearChatContext: () => void;
  clearContext: () => void;
  logError: (error: Error | string, options?: {
    source?: ErrorSource;
    severity?: ErrorSeverity;
    agentId?: string;
    deploymentId?: string;
    vpsInstanceId?: string;
    context?: Partial<ErrorContext>;
  }) => Promise<void>;
  logFrontendError: (error: Error | string, context?: Partial<ErrorContext>) => Promise<void>;
  logProvisioningError: (error: Error | string, vpsInstanceId?: string, context?: Partial<ErrorContext>) => Promise<void>;
  logDeploymentError: (error: Error | string, deploymentId?: string, context?: Partial<ErrorContext>) => Promise<void>;
}

const ErrorTrackingContext = createContext<ErrorTrackingContextValue | null>(null);

export function ErrorTrackingProvider({ children }: { children: ReactNode }) {
  useEffect(() => {
    // Setup global error handlers on mount
    setupGlobalErrorHandlers();
    
    // Cleanup on unmount
    return () => {
      clearErrorContext();
    };
  }, []);

  const value: ErrorTrackingContextValue = {
    setContext: setErrorContext,
    addChatMessage,
    clearChatContext,
    clearContext: clearErrorContext,
    logError,
    logFrontendError,
    logProvisioningError,
    logDeploymentError,
  };

  return (
    <ErrorTrackingContext.Provider value={value}>
      {children}
    </ErrorTrackingContext.Provider>
  );
}

export function useErrorTracking() {
  const context = useContext(ErrorTrackingContext);
  if (!context) {
    throw new Error('useErrorTracking must be used within ErrorTrackingProvider');
  }
  return context;
}
